import { createSelector } from 'reselect';
import isEqual from 'lodash/isEqual';
import { isNumeric } from '../../js/app/react/validators';
import {
  WHAT_IFS_GOAL_DICTIONARY,
  BASE_CLIENT_OBJECT,
  BASE_GOAL_OBJECT,
  INSURANCE_GOAL_TYPE,
  WHAT_IFS_WITH_NO_PLAN_IMPACT,
  WHAT_IFS_WITH_SHELL,
} from './WhatIfsConstants';
import { selectedGoal as selectedFinancialPlanGoal } from '../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { selectedInsuranceTypeOrDefaultSelector } from '../FinancialPlan/Insurance/InsuranceSelector';
import { isCurrentPlanSelector } from '../PlanSelectionNewWhatIfs/PlanSelectionNewWhatIfsSelector';
import WHAT_IFS_URL_DICTIONARY from './WhatIfsUrlDictionary';
import { currentClientIdSelector } from '../CommonSelectors/CommonAreaSelector';

export const financialPlanSelector = state => state.FinancialPlan;

export const clientFinancialPlanOrDefaultSelector = createSelector(
  currentClientIdSelector,
  financialPlanSelector,
  (clientId, financialPlan) => financialPlan[clientId] || {},
);

export const nullableFpIdSelector = createSelector(
  clientFinancialPlanOrDefaultSelector,
  clientFinancialPlanOrDefault => clientFinancialPlanOrDefault.fpId || null,
);

export const whatIfsSelector = state => state.WhatIfs;

export const nullableWhatIfsSelector = createSelector(
  currentClientIdSelector,
  whatIfsSelector,
  (clientId, whatIfsClientDictionary) => whatIfsClientDictionary[clientId] || null,
);
export const whatIfs = nullableWhatIfsSelector;
export const whatIfsOrDefaultSelector = createSelector(
  nullableWhatIfsSelector,
  nullableWhatIfs => nullableWhatIfs || BASE_CLIENT_OBJECT,
);

export const visible = createSelector(
  whatIfsOrDefaultSelector,
  nullableFpIdSelector,
  (whatIfsOrDefault, nullableFpId) => !!nullableFpId && !!whatIfsOrDefault.visible,
);

export const nullableCompareGoalsSelector = createSelector(
  whatIfsOrDefaultSelector,
  whatIfsOrDefault => whatIfsOrDefault.compareGoals || null,
);

export const compareGoalsDataOrDefaultSelector = createSelector(
  nullableCompareGoalsSelector,
  nullableCompareGoals => {
    if (nullableCompareGoals) {
      return nullableCompareGoals.data || {};
    }
    return {};
  },
);

export const compareGoalsIsDataLoadedSelector = createSelector(
  nullableCompareGoalsSelector,
  nullableCompareGoals => {
    if (nullableCompareGoals) {
      return (
        !!nullableCompareGoals.data &&
        !!nullableCompareGoals.data.GoalsAdditionalSavingsWhatIfs &&
        !!nullableCompareGoals.data.GoalsAdditionalSavingsWhatIfs.length
      );
    }
    return false;
  },
);

export const compareGoalsOpenSelector = createSelector(
  nullableCompareGoalsSelector,
  nullableCompareGoals => (nullableCompareGoals ? nullableCompareGoals.open || false : false),
);

export const detailedAssetAllocationOpenSelector = createSelector(
  whatIfsOrDefaultSelector,
  whatIfsOrDefault => whatIfsOrDefault.detailedAssetAllocationOpen || false,
);

export const loading = createSelector(
  whatIfsOrDefaultSelector,
  whatIfsOrDefault => !!whatIfsOrDefault.loading,
);

export const planImpactLoadingSelector = createSelector(
  whatIfsOrDefaultSelector,
  whatIfsOrDefault => !!whatIfsOrDefault.planImpactLoading,
);

export const popupLoadingSelector = createSelector(
  whatIfsOrDefaultSelector,
  whatIfsOrDefault => !!whatIfsOrDefault.popupLoading,
);

export const nullableSelectedGoalIdSelector = createSelector(
  selectedFinancialPlanGoal,
  nullableSelectedGoal => {
    if (nullableSelectedGoal) {
      return nullableSelectedGoal.GoalId || null;
    }
    return null;
  },
);

export const nullableSelectedGoalWhatIfSelector = createSelector(
  nullableSelectedGoalIdSelector,
  nullableWhatIfsSelector,
  (nullableGoalId, nullableWhatIfs) => {
    if (nullableWhatIfs && nullableGoalId) {
      return nullableWhatIfs[nullableGoalId] || null;
    }
    return null;
  },
);

export const selectedGoalWhatIfOrDefaultSelector = createSelector(
  nullableSelectedGoalWhatIfSelector,
  nullableGoalWhatIf => nullableGoalWhatIf || BASE_GOAL_OBJECT,
);
export const goalWhatIf = selectedGoalWhatIfOrDefaultSelector;

export const nullableCurrentViewIdSelector = createSelector(
  selectedGoalWhatIfOrDefaultSelector,
  selectedFinancialPlanGoal,
  selectedInsuranceTypeOrDefaultSelector,
  isCurrentPlanSelector,
  (
    selectedGoalWhatIfOrDefault,
    nullableSelectedGoal,
    selectedInsuranceTypeOrDefault,
    isCurrentPlan,
  ) => {
    if (isCurrentPlan) {
      return null;
    }
    const { currentViewId } = selectedGoalWhatIfOrDefault;
    if (!isNumeric(currentViewId) && nullableSelectedGoal) {
      if (nullableSelectedGoal.GoalType === INSURANCE_GOAL_TYPE) {
        return (
          WHAT_IFS_GOAL_DICTIONARY[
            `${nullableSelectedGoal.GoalType}.${selectedInsuranceTypeOrDefault}`
          ] || null
        );
      }
      return WHAT_IFS_GOAL_DICTIONARY[nullableSelectedGoal.GoalType] || null;
    }
    return isNumeric(currentViewId) ? +currentViewId : null;
  },
);
export const currentViewIdSelector = nullableCurrentViewIdSelector;

function hasPlanImpact(viewId) {
  if (viewId < 0) {
    return false;
  }
  if (!window.unsafeDoesntHavePlanImpactMap) {
    window.unsafeDoesntHavePlanImpactMap = {};
    WHAT_IFS_WITH_NO_PLAN_IMPACT.forEach(whatIfType => {
      window.unsafeDoesntHavePlanImpactMap[whatIfType] = true;
    });
  }
  return !window.unsafeDoesntHavePlanImpactMap[viewId];
}

export const hasPlanImpactSelector = createSelector(
  nullableCurrentViewIdSelector,
  nullableCurrentViewId => {
    if (nullableCurrentViewId === null) {
      return false;
    }
    return hasPlanImpact(nullableCurrentViewId);
  },
);

function hasShell(viewId) {
  if (!window.unsafeHasShellMap) {
    window.unsafeHasShellMap = {};
    WHAT_IFS_WITH_SHELL.forEach(whatIfType => {
      window.unsafeHasShellMap[whatIfType] = true;
    });
  }
  return window.unsafeHasShellMap[viewId];
}

export const hasShellSelector = createSelector(
  nullableCurrentViewIdSelector,
  nullableCurrentViewId => {
    if (nullableCurrentViewId === null) {
      return false;
    }
    return hasShell(nullableCurrentViewId);
  },
);

export const nullableWhatIfTypeSelector = createSelector(
  selectedGoalWhatIfOrDefaultSelector,
  selectedGoalWhatIfOrDefault => {
    const { currentViewId } = selectedGoalWhatIfOrDefault;
    return isNumeric(currentViewId) ? +currentViewId : null;
  },
);

export const nullableUrlSelector = createSelector(
  selectedFinancialPlanGoal,
  selectedInsuranceTypeOrDefaultSelector,
  (nullableSelectedGoal, selectedInsuranceTypeOrDefault) => {
    if (nullableSelectedGoal) {
      if (nullableSelectedGoal.GoalType === INSURANCE_GOAL_TYPE) {
        return (
          WHAT_IFS_URL_DICTIONARY[
            `${nullableSelectedGoal.GoalType}.${selectedInsuranceTypeOrDefault}`
          ] || null
        );
      }
      return WHAT_IFS_URL_DICTIONARY[nullableSelectedGoal.GoalType] || null;
    }
    return null;
  },
);

export const selectedSection = createSelector(
  selectedGoalWhatIfOrDefaultSelector,
  selectedGoalWhatIfOrDefault => selectedGoalWhatIfOrDefault.selectedSection,
);

export const whatIfsSectionsSelector = createSelector(
  selectedGoalWhatIfOrDefaultSelector,
  selectedGoalWhatIfOrDefault => selectedGoalWhatIfOrDefault.whatIfsSections,
);

export const whatIfsTemplateSectionsSelector = createSelector(
  selectedGoalWhatIfOrDefaultSelector,
  selectedGoalWhatIfOrDefault => selectedGoalWhatIfOrDefault.whatIfsTemplateSections,
);

export const keysDictionarySelectorFunction = whatIfsSections => {
  const result = {};
  const data = whatIfsSections;
  Object.keys(data).forEach(key => {
    (data[key] || []).forEach(whatIf => {
      result[whatIf.WhatIf.WhatIfType] = {
        ...whatIf,
        GroupTitle: key,
      };
    });
  });
  return result;
};

export const whatIfTypesDictionarySelector = createSelector(
  whatIfsSectionsSelector,
  keysDictionarySelectorFunction,
);

export const whatIfTemplateKeysDictionarySelector = createSelector(
  whatIfsTemplateSectionsSelector,
  keysDictionarySelectorFunction,
);

export const whatIfOrDefaultSelector = createSelector(
  whatIfTypesDictionarySelector,
  nullableWhatIfTypeSelector,
  (whatIfTypesDictionaryOrDefault, nullablewhatIfType) =>
    whatIfTypesDictionaryOrDefault[nullablewhatIfType] || {},
);

export const whatIfTemplateOrDefaultSelector = createSelector(
  whatIfTemplateKeysDictionarySelector,
  nullableWhatIfTypeSelector,
  (whatIfTemplateKeysDictionary, nullablewhatIfType) =>
    whatIfTemplateKeysDictionary[nullablewhatIfType] || {},
);

export const whatIfIsAppliedSelector = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  return whatIf.IsApplied;
};

export const whatIfInnerSelector = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  const innerWhatIf = whatIf.WhatIf || {};
  return innerWhatIf;
};
export const whatIfTemplateInnerSelector = (state, props) => {
  const whatIf = whatIfTemplateOrDefaultSelector(state, props);
  const innerWhatIf = whatIf.WhatIf || {};
  return innerWhatIf;
};

export const whatIfTypeSelector = (state, props) => {
  const whatIf = whatIfTemplateOrDefaultSelector(state, props);
  const innerWhatIf = whatIf.WhatIf || {};
  return innerWhatIf.WhatIfType;
};

export const whatIfKeySelector = (state, props) => {
  const whatIf = whatIfTemplateOrDefaultSelector(state, props);
  const innerWhatIf = whatIf.WhatIf || {};
  return innerWhatIf.WhatIfKey;
};

export const impactSelector = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  return whatIf.Impact || [];
};

export const nullableTitleSelector = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  return whatIf.DefaultTitle || null;
};

export const nullableGroupTitleSelector = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  if (whatIf.GroupTitle) {
    return whatIf.GroupTitle.split(/(?=[A-Z])/).join(' ');
  }
  return null;
};

export const areOptionsLoaded = createSelector(whatIfOrDefaultSelector, whatIf => !!whatIf.Options);

export const whatIfOptionsOrDefaultSelector = createSelector(
  whatIfOrDefaultSelector,
  whatIfOrDefault => whatIfOrDefault.Options || {},
);

export const open = createSelector(
  whatIfsOrDefaultSelector,
  nullableCurrentViewIdSelector,
  (whatIfsOrDefault, nullableCurrentViewId) =>
    whatIfsOrDefault.open && nullableCurrentViewId !== null,
);

export const isWhatIfDirtySelector = createSelector(
  whatIfTemplateInnerSelector,
  whatIfInnerSelector,
  (template, current) => !isEqual(template, current),
);

export const isOPLMonteCarloSelector = createSelector(
  whatIfsSelector,
  ({ isMonteCarloActive }) => isMonteCarloActive,
);
